<template>
  <div>
    <portal to="subheader-left">
      <ul class="nav nav-light-primary nav-bold nav-pills py-2" v-if="isValidReiseterminId">
        <router-link
          custom
          :to="{ name: 'reisetermine-view', params: { reiseterminId: reiseterminId } }"
          v-slot="{ href, navigate, isActive, isExactActive }"
        >
          <li class="nav-item">
            <a
              class="nav-link px-xs-3 px-lg-4"
              :class="{ 'menu-item-active': isActive, active: $route.name === 'reisetermine-view' }"
              :href="href"
              @click="navigate"
            >
              <span>Details</span>
            </a>
          </li>
        </router-link>
        <router-link
          custom
          :to="{ name: 'reisetermin-view-releasepruefung', params: { reiseterminId: reiseterminId } }"
          v-slot="{ href, navigate, isActive, isExactActive }"
        >
          <li class="nav-item">
            <a
              class="nav-link px-xs-3 px-lg-4"
              :class="{
                'menu-item-active': isActive,
                active: $route.name === 'reisetermin-view-releasepruefung',
              }"
              :href="href"
              @click="navigate"
            >
              <span>Releaseprüfung</span>
            </a>
          </li>
        </router-link>
      </ul>
    </portal>
    <div ref="intersectWatcher"></div>

    <b-alert v-if="!isValidReiseterminId" show variant="danger" class="m-5">
      <h4>Reisetermin nicht gefunden</h4>
      <p>Die Reisetermin-ID in der URL ist ungültig. Bitte überprüfe den Link.</p>
      <router-link :to="{ name: 'reisetermine-overview' }">Zurück zur Übersicht</router-link>
    </b-alert>

    <template v-if="isValidReiseterminId">
      <ReiseterminViewHeader
        :isInitialLoading="isInitialLoading"
        :reiseterminObj="reiseterminObj"
      ></ReiseterminViewHeader>

      <ReiseterminViewDetailHeaderCompact
        v-if="!$store.state.userSettingsLocal.alwaysHideReiseterminCompactHeader"
        v-show="showCompactHeader"
        :isInitialLoading="isInitialLoading"
        :reiseterminObj="reiseterminObj"
        @triggerPressed="loadData"
      ></ReiseterminViewDetailHeaderCompact>

      <router-view
        :reiseterminObj="reiseterminObj"
        @updated-vorgang="loadData"
        @releaseprocess-updated="releaseProzessVorgaengeUpdate"
      ></router-view>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import * as odataService from '@/core/common/services/odata.service';
import EventBus from '@/core/common/plugins/event-bus.js';
import { VISITED_REISETERMIN } from '@/core/user/stores/user-settings-local.module';
import { getReleaseprozessStatus } from '@/core/produkte/reisetermine/stores/reisetermine.module';
import ReiseterminViewHeader from '@/components/produkte/reisetermine/reisetermin-view-header.vue';
import ReiseterminViewDetailHeaderCompact from '@/components/produkte/reisetermine/reisetermin-view-detail-header-compact.vue';

export default {
  name: 'ReisetermineViewComponent',
  components: {
    ReiseterminViewHeader,
    ReiseterminViewDetailHeaderCompact,
  },
  props: {
    reiseterminId: Number,
  },
  data() {
    return {
      observer: null,
      showCompactHeader: false,
      isInitialLoading: false,
      isLoading: false,
      reiseterminObj: null,
    };
  },
  created() {
    this.observer = new IntersectionObserver(this.onElementObserved, {
      root: this.$el,
    });
  },
  destroyed() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  watch: {
    triggerSent() {
      //if state has changed update reisetermin
      this.loadData();
    },
    reiseterminId() {
      if (!this.isValidReiseterminId) return;
      this.isInitialLoading = true;
      this.loadData();
    },
  },
  computed: {
    ...mapState({
      triggerSent: state => state.workflowManagerReisetermin.triggerSent,
    }),
    isValidReiseterminId() {
      return Number.isFinite(this.reiseterminId) && this.reiseterminId > 0;
    },
  },
  beforeDestroy() {
    EventBus.$off('reload');
  },
  mounted() {
    if (!this.isValidReiseterminId) return;
    this.isInitialLoading = true;
    this.loadData();
    this.observer.observe(this.$refs['intersectWatcher']);
    EventBus.$on('reload', () => {
      this.loadData();
    });
  },
  methods: {
    onAufgabeBearbeitet(changedAufgabe) {
      this.reiseterminObj.aufgaben = this.reiseterminObj.aufgaben.map(aufgabe => {
        if (aufgabe.id === changedAufgabe.id) return changedAufgabe;
        return aufgabe;
      });
    },
    onElementObserved(element) {
      this.showCompactHeader = element[0].intersectionRatio === 0;
    },

    async loadData() {
      this.isLoading = true;
      this.reiseterminObj = await this.getReisetermin();
      this.isLoading = false;
    },
    // Die Prüf-Flags liegen am Reisetermin selbst, die Icon-Stati an der Route releaseprozessStatus
    releaseProzessVorgaengeUpdate() {
      const expand = {
        comments: {},
        pax: {},
        Metadata: { expand: { ZiellandZielflughaefen: {}, ZiellandAbflughaefen: {}, Inlandsfluege: {} } },
        reise: {
          expand: {
            konfigurationen: {},
          },
        },
        gueltigeKonfiguration: {},
      };
      const filter = [{ id: parseInt(this.reiseterminId) }];
      return Promise.all([
        odataService.getReisetermin({ filter, expand }),
        getReleaseprozessStatus(this.reiseterminId).catch(() => null),
      ]).then(([response, releaseprozessStatus]) => {
        this.reiseterminObj.reiseunterlagenGeprueft = response.data[0].reiseunterlagenGeprueft;
        this.reiseterminObj.transferplanungGeprueft = response.data[0].transferplanungGeprueft;
        this.reiseterminObj.hotelsUndProgrammGeprueft = response.data[0].hotelsUndProgrammGeprueft;
        this.reiseterminObj.releaseprozessStatus = releaseprozessStatus?.reisetermin || null;
      });
    },
    async getReisetermin() {
      const reiseterminFilter = { id: parseInt(this.reiseterminId) };
      const expand = {
        comments: {},
        pax: {},
        flugeinkauf: { expand: { Gruppeneinkaeufe: {} } },
        aufgaben: {
          expand: {
            comments: {},
            vorgaenge: {
              select: ['id'],
              expand: {
                teilnehmers: {
                  filter: { deletedAt: null },
                  select: ['name'],
                },
              },
            },
          },
        },
        Metadata: { expand: { ZiellandZielflughaefen: {}, ZiellandAbflughaefen: {}, Inlandsfluege: {} } },
        reise: {
          expand: {
            konfigurationen: {},
          },
        },
        gueltigeKonfiguration: {},
        ersatztermin: {},
        ursprungsreisetermin: {},
      };
      const filter = [reiseterminFilter];
      return Promise.all([
        odataService.getReisetermin({ filter, expand }),
        getReleaseprozessStatus(this.reiseterminId).catch(() => null),
      ])
        .then(([response, releaseprozessStatus]) => {
          this.$store.dispatch(VISITED_REISETERMIN, {
            id: response.data[0].id,
            kuerzel: response.data[0].reiseterminkuerzel,
          });
          return { ...response.data[0], releaseprozessStatus: releaseprozessStatus?.reisetermin || null };
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.isInitialLoading = false;
        });
    },
  },
  beforeRouteEnter(to, from, next) {
    document.title += ' / ' + to.params.reiseterminId;
    next();
  },
  beforeRouteUpdate(to, from, next) {
    document.title += ' / ' + to.params.reiseterminId;
    next();
  },
};
</script>

<style lang="scss" scoped>
.more-btn {
  cursor: pointer;
}
</style>
